import { useMemo, useState } from "react";
import {
  Car, Clock, Bike, Heart, Info, ShoppingBasket, MapPin, Navigation, Search,
} from "lucide-react";
import {
  ISTRES_PARKINGS, ISTRES_ZONES_BLEUES, ISTRES_DEPOSE_MINUTE,
  ISTRES_DELIVERY, ISTRES_KIOSKS, ISTRES_DEFIBRILLATORS, ISTRES_MARKETS,
  itineraryUrl,
} from "@/lib/istres";

const CATS = [
  { id: "parking", label: "Parkings", color: "bg-plum-700 text-white", chip: "text-plum-700 bg-plum-100", icon: Car, source: () => ISTRES_PARKINGS.map((x) => ({ ...x, _meta: `${x.capacity} places · ${x.type}`, _hours: x.hours })) },
  { id: "zone-bleue", label: "Zones bleues", color: "bg-blue-500 text-white", chip: "text-blue-700 bg-blue-100", icon: Clock, source: () => ISTRES_ZONES_BLEUES.map((x) => ({ ...x, _meta: `${x.duration} · ${x.note}`, _hours: x.hours })) },
  { id: "depose-minute", label: "Dépose-minute", color: "bg-orange-500 text-white", chip: "text-orange-700 bg-orange-100", icon: Clock, source: () => ISTRES_DEPOSE_MINUTE.map((x) => ({ ...x, _meta: `${x.max} · ${x.note}`, _hours: x.hours })) },
  { id: "delivery", label: "Livraison", color: "bg-mint-500 text-white", chip: "text-mint-600 bg-mint-100", icon: Bike, source: () => ISTRES_DELIVERY.map((x) => ({ ...x, _meta: x.zone, _hours: null })) },
  { id: "defib", label: "Défibrillateurs", color: "bg-red-500 text-white", chip: "text-red-600 bg-red-100", icon: Heart, source: () => ISTRES_DEFIBRILLATORS.map((x) => ({ ...x, _meta: x.location || "Public", _hours: x.access || "24h/24" })) },
  { id: "kiosk", label: "Bornes info", color: "bg-pink-500 text-white", chip: "text-pink-600 bg-pink-100", icon: Info, source: () => ISTRES_KIOSKS.map((x) => ({ ...x, _meta: x.purpose || "Information", _hours: x.hours })) },
  { id: "market", label: "Marchés", color: "bg-olive-500 text-white", chip: "text-olive-700 bg-olive-100", icon: ShoppingBasket, source: () => ISTRES_MARKETS.map((x) => ({ ...x, _meta: `${x.day} · ${x.where}`, _hours: x.hours })) },
];

function focusOnMap(lat, lng) {
  if (lat == null || lng == null) return;
  window.dispatchEvent(new CustomEvent("istres-map:focus", { detail: { lat, lng, zoom: 17 } }));
  const el = document.getElementById("plan");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function CityMiniDirectory() {
  const [active, setActive] = useState("parking");
  const [query, setQuery] = useState("");

  const counts = useMemo(() => {
    const m = {};
    CATS.forEach((c) => { try { m[c.id] = c.source().length; } catch { m[c.id] = 0; } });
    return m;
  }, []);

  const items = useMemo(() => {
    const cat = CATS.find((c) => c.id === active);
    if (!cat) return [];
    const q = query.trim().toLowerCase();
    let list = cat.source();
    if (q) {
      list = list.filter((x) => `${x.name} ${x._meta || ""}`.toLowerCase().includes(q));
    }
    return list;
  }, [active, query]);

  const activeCat = CATS.find((c) => c.id === active);

  return (
    <div className="mt-6 sm:mt-8" data-testid="city-mini-directory">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="heading-display text-[1.3rem] sm:text-[1.6rem] text-plum-900 leading-tight">
            Trouvez en liste ce qui est sur la carte.
          </h3>
          <p className="text-[13px] sm:text-[14px] text-plum-700/70 mt-1">
            Pratique sur mobile : tapez une catégorie, parcourez, lancez l&apos;itinéraire.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-plum-700/40" strokeWidth={2.2} />
        <input
          data-testid="mini-directory-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Rechercher dans « ${activeCat?.label || ""} »...`}
          className="w-full h-11 pl-11 pr-4 rounded-2xl bg-white border border-cream-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-plum-500/30 focus:border-plum-300"
        />
      </div>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }} data-testid="mini-directory-cats">
        {CATS.map((c) => {
          const isActive = c.id === active;
          return (
            <button
              key={c.id}
              onClick={() => { setActive(c.id); setQuery(""); }}
              data-testid={`mini-cat-${c.id}`}
              className={`shrink-0 h-10 px-4 rounded-full text-[13px] font-bold transition-all flex items-center gap-1.5 ${
                isActive ? c.color + " shadow-md" : "bg-white text-plum-700 border border-cream-200 hover:bg-cream-50"
              }`}
            >
              <c.icon size={13} strokeWidth={2.4} />
              {c.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] tabular-nums ${isActive ? "bg-black/15" : "bg-cream-100 text-plum-700/70"}`}>{counts[c.id]}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="phenix-card p-8 text-center">
          <Search size={24} className="mx-auto text-plum-700/30 mb-2" />
          <p className="text-[13px] text-plum-700/60">Aucun résultat dans cette catégorie.</p>
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-2.5" data-testid="mini-directory-list">
          {items.map((it) => (
            <li key={it.id}>
              <article className="phenix-card phenix-card-hover p-4 flex items-start gap-3" data-testid={`mini-item-${it.id}`}>
                <button
                  onClick={() => focusOnMap(it.lat, it.lng)}
                  className={`w-11 h-11 rounded-full ${activeCat.color} grid place-items-center shrink-0 shadow-sm active:scale-95 transition`}
                  title="Voir sur la carte"
                  aria-label="Voir sur la carte"
                >
                  <activeCat.icon size={18} strokeWidth={2.2} />
                </button>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => focusOnMap(it.lat, it.lng)}
                    className="text-left w-full"
                    data-testid={`mini-focus-${it.id}`}
                  >
                    <h4 className="font-display font-bold text-[15px] text-plum-900 leading-tight line-clamp-1 hover:text-plum-700">{it.name}</h4>
                  </button>
                  {it._meta && <p className="text-[12px] text-plum-700/65 line-clamp-1 mt-0.5">{it._meta}</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                    {it._hours && (
                      <span className="inline-flex items-center gap-1 text-plum-700/65">
                        <Clock size={11} strokeWidth={2.2} className="text-plum-500" /> {it._hours}
                      </span>
                    )}
                    {it.lat != null && it.lng != null && (
                      <>
                        <button
                          onClick={() => focusOnMap(it.lat, it.lng)}
                          className="inline-flex items-center gap-1 font-bold text-plum-700 hover:text-plum-800"
                          data-testid={`mini-onmap-${it.id}`}
                        >
                          <MapPin size={11} strokeWidth={2.5} /> Sur la carte
                        </button>
                        <a
                          href={itineraryUrl(it.lat, it.lng, it.name)}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`mini-itineraire-${it.id}`}
                          className="ml-auto inline-flex items-center gap-1 font-bold text-plum-700 hover:text-plum-800 active:scale-95 transition"
                        >
                          <Navigation size={11} strokeWidth={2.5} /> Itinéraire
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
